import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { Check } from "lucide-react";
import { CartContext } from "./CartContext";

const BulkShop = () => {
  const { 
    minOrderAmounts 
  } = useContext(CartContext);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    state: "",
    city: "",
    address: "",
    product: "",
    quantity: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = "919728622667";
    const msg = encodeURIComponent(
      `*Bulk Order Enquiry*\n\nName: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nState: ${formData.state}\nCity: ${formData.city}\nProduct: ${formData.product}\nQuantity: ${formData.quantity}\nMessage: ${formData.message}\nAddress: ${formData.address}`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${msg}`, "_blank");
    setSubmitted(true);
  };

  const bulkBenefits = [
    { icon: "📦", title: "Wholesale Rates", desc: "Best prices for bulk orders directly from Sivakasi" },
    { icon: "🚚", title: "Free Delivery", desc: "Free shipping on bulk orders above ₹50,000" },
    { icon: "🎯", title: "Custom Packing", desc: "Customized packing as per your requirements" },
    { icon: "⭐", title: "Quality Assured", desc: "100% original and premium quality products" },
  ];

  return (
    <div className="bg-[#FAF8F5] min-h-screen font-sans">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-950 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-black font-serif mb-4"
          >
            Bulk & Wholesale Orders
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-blue-200 max-w-2xl mx-auto"
          >
            Get the best wholesale rates for bulk orders. Direct from Sivakasi - India's fireworks capital!
          </motion.p>
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {bulkBenefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl p-5 shadow-lg border border-blue-100 text-center"
            >
              <span className="text-3xl">{benefit.icon}</span>
              <h3 className="font-black text-sm mt-2 text-gray-800">{benefit.title}</h3>
              <p className="text-[11px] text-gray-500 mt-1 font-medium">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          
          {/* Left: Enquiry Form */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-2xl font-black text-gray-800 mb-6 font-serif">Send Bulk Enquiry</h2>
            
            {submitted ? (
              <div className="text-center py-12 space-y-4">
                <span className="text-6xl">✅</span>
                <h3 className="text-xl font-black text-green-700">Enquiry Submitted!</h3>
                <p className="text-sm text-gray-500">
                  We'll contact you shortly on WhatsApp with the best bulk pricing.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-blue-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-800 transition"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="10-digit number"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">State *</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none bg-white"
                    >
                      <option value="">Select State</option>
                      {Object.keys(minOrderAmounts).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City name"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Product Interested In *</label>
                    <input
                      type="text"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Sparklers, Rockets"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Approx. Quantity *</label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      placeholder="e.g. 100 boxes"
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Delivery Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Full address with pincode"
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Additional Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Any specific requirements..."
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-900 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 text-sm"
                >
                  <FaWhatsapp size={18} />
                  <span>Send Enquiry via WhatsApp</span>
                </button>
              </form>
            )}
          </div>

          {/* Right: Info Panel */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-950 to-indigo-950 rounded-3xl p-8 text-white">
              <h3 className="text-xl font-black font-serif mb-4">Why Buy Bulk From Us?</h3>
              <ul className="space-y-4">
                {[
                  "Direct manufacturer pricing - no middlemen",
                  "Free delivery on orders above ₹50,000",
                  "Custom branding & packaging options",
                  "Dedicated account manager assigned",
                  "Flexible payment terms for bulk buyers",
                  "Pan-India delivery with insurance",
                  "GST invoices for business orders",
                  "Sample testing available before bulk order"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Contact */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-black text-gray-800 mb-4">Quick Contact</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <FaWhatsapp size={20} className="text-green-600" />
                  <div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">WhatsApp</p>
                    <a href="https://wa.me/919728622667" className="font-bold text-gray-800 hover:text-green-700">+91 9728622667</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <span className="text-lg">📞</span>
                  <div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Toll Free</p>
                    <p className="font-bold text-gray-800">1800-XXX-XXXX</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                  <span className="text-lg">📧</span>
                  <div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Email</p>
                    <p className="font-bold text-gray-800">bulk@tskcrackers.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkShop;